import type { StepTime } from '@compass/client/types/cases_analytics';

/**
 * There is no submission step: the state it was keyed on, `delivering_email`, is emitted
 * nowhere in the ledger, and the query already bills the time from review to submitted to
 * Review.
 */
export const STEP_ORDER = [
  'call_received',
  'intake_unassigned',
  'intake_assigned',
  'review',
] as const;

export type StepKey = (typeof STEP_ORDER)[number];

export const STEP_LABELS: Record<StepKey, string> = {
  call_received: 'Call Received',
  intake_unassigned: 'Intake Draft (Unassigned)',
  intake_assigned: 'Intake Draft (Assigned)',
  review: 'Review',
};

/** Tier 1 is the call being received; Tier 2 is everything the team does after it. */
export const STEP_TIER: Record<StepKey, 1 | 2> = {
  call_received: 1,
  intake_unassigned: 2,
  intake_assigned: 2,
  review: 2,
};

/** How the legend and the chart series name a step: "Tier 2 · Review". */
export const stepSeriesLabel = (step: StepKey): string =>
  `Tier ${STEP_TIER[step]} · ${STEP_LABELS[step]}`;

/** The three case sets the Summary tab compares, in the order it shows them. */
const SEGMENT_ORDER = ['all', 'within_sla', 'breached'] as const;

export type SegmentKey = (typeof SEGMENT_ORDER)[number];

/** Named as the SLA status filter names them. */
export const SEGMENT_LABELS: Record<SegmentKey, string> = {
  all: 'All Cases',
  within_sla: 'Within SLA',
  breached: 'SLA Breached',
};

interface Segments {
  all?: number;
  within_sla?: number;
  breached?: number;
}

export interface StepRow extends Segments {
  step: StepKey;
  /** Breached minus within SLA, when both are known. */
  difference?: number;
}

/** One bar of the Summary tab: a case set, its time at each step, and its tier totals. */
export interface SegmentRow {
  segment: SegmentKey;
  /** Average seconds at each step, in `STEP_ORDER`; undefined where the set has no row. */
  steps: (number | undefined)[];
  tier1?: number;
  tier2?: number;
  total?: number;
  /** Cases behind the averages. */
  caseCount: number;
}

const isStepKey = (step: string | undefined): step is StepKey =>
  STEP_ORDER.includes(step as StepKey);
const isSegment = (segment: string | undefined): segment is SegmentKey =>
  SEGMENT_ORDER.includes(segment as SegmentKey);

const sumKnown = (values: (number | undefined)[]): number | undefined => {
  const known = values.filter((v): v is number => v !== undefined);
  return known.length ? known.reduce((a, b) => a + b, 0) : undefined;
};

const tierTotal = (steps: (number | undefined)[], tier: 1 | 2): number | undefined =>
  sumKnown(steps.filter((_, i) => STEP_TIER[STEP_ORDER[i]] === tier));

/**
 * Arranges the Summary tab: one row per step in order, one bar per case set, and the Tier 1
 * and Tier 2 totals of each set.
 */
export const summarizeSteps = (
  summary: StepTime[] | undefined,
): { rows: StepRow[]; segments: SegmentRow[]; tier1: Segments; tier2: Segments } => {
  const seconds = new Map<SegmentKey, Map<StepKey, number>>();
  const cases = new Map<SegmentKey, number>();
  for (const item of summary ?? []) {
    if (!isStepKey(item.step) || !isSegment(item.segment)) continue;
    const bySteps = seconds.get(item.segment) ?? new Map<StepKey, number>();
    bySteps.set(item.step, item.avg_seconds ?? 0);
    seconds.set(item.segment, bySteps);
    // A case missing a step is left out of the query altogether, so every step counts the same
    // cases; the largest count is that number, and still reads right if one step is short.
    cases.set(item.segment, Math.max(cases.get(item.segment) ?? 0, item.case_count ?? 0));
  }

  const rows: StepRow[] = STEP_ORDER.map(step => {
    const at = (segment: SegmentKey) => seconds.get(segment)?.get(step);
    const within_sla = at('within_sla');
    const breached = at('breached');
    return {
      step,
      all: at('all'),
      within_sla,
      breached,
      difference:
        breached !== undefined && within_sla !== undefined ? breached - within_sla : undefined,
    };
  });

  const segments: SegmentRow[] = SEGMENT_ORDER.map(segment => {
    const steps = STEP_ORDER.map(step => seconds.get(segment)?.get(step));
    const tier1 = tierTotal(steps, 1);
    const tier2 = tierTotal(steps, 2);
    return {
      segment,
      steps,
      tier1,
      tier2,
      total: sumKnown([tier1, tier2]),
      caseCount: cases.get(segment) ?? 0,
    };
  });

  const tierOf = (tier: 'tier1' | 'tier2'): Segments =>
    Object.fromEntries(segments.map(s => [s.segment, s[tier]]));

  return { rows, segments, tier1: tierOf('tier1'), tier2: tierOf('tier2') };
};
